// FormBuilder.jsx
import React, { useState } from 'react';
import { DatePicker, Modal, Button, Form, Input, Select, Space, Card, Tooltip, ConfigProvider, Segmented } from 'antd';
import { PlusOutlined, DeleteOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import axios from 'axios';


const { Option } = Select;

const PricingTiersBuilder = ({ isVisible, onCancel, onSave, setPrice, activePromoCodes, orgName }) => {
    const [page, setPage] = useState("Pricing Tiers");

    const [tiers, setTiers] = useState([]);
    const [codes, setCodes] = useState([]);

    const addTier = () => {
        setTiers([
            ...tiers,
            {
                id: Date.now(),
                name: '',
                description: '',
                price: '',
                expiration: ''
            }
        ]);
    };

    const addCode = () => {
        setCodes([
            ...codes,
            {
                id: Date.now(),
                name: '',
                code: '',
                discount: '',
                discountType: "$",
                expiration: ''
            }
        ]);
    };

    const removeTier = (tierId) => {
        setTiers(tiers.filter(q => q.id !== tierId));
    };

    const removeCode = (codeId) => {
        setCodes(codes.filter(c => c.id !== codeId));
    };

    const moveTier = (index, direction) => {
        const newTiers = [...tiers];
        const temp = newTiers[index];
        newTiers[index] = newTiers[index + direction];
        newTiers[index + direction] = temp;
        setTiers(newTiers);
    };

    const updateTier = (tierId, field, value) => {
        console.log(value)
        setTiers(tiers.map(q =>
            q.id === tierId ? { ...q, [field]: value } : q
        ));
    };

    const updateCode = (codeId, field, value) => {
        console.log(value)
        setCodes(codes.map(c =>
            c.id === codeId ? { ...c, [field]: value } : c
        ));
    };

    const handleTierDateChange = (tierId, date) => {
        // date will be null when cleared or a dayjs object when selected
        // const formattedDate = date ? date.format('YYYY-MM-DD') : '';
        const formattedDate = date ? date.toISOString().split('T')[0] : '';
        updateTier(tierId, 'expiration', formattedDate);
    };

    const handleCodeDateChange = (codeId, date) => {
        // date will be null when cleared or a dayjs object when selected
        // const formattedDate = date ? date.format('YYYY-MM-DD') : '';
        const formattedDate = date ? date.toISOString().split('T')[0] : '';
        updateCode(codeId, 'expiration', formattedDate);
    };

    const handleSave = async () => {
        // Check for tiers that have either name or price but not both
        const incompleteTiers = tiers.filter(tier =>
            (!tier.name || tier.name.trim() === '') !== (!tier.price || tier.price.trim() === '')
        );

        if (incompleteTiers.length > 0) {
            Modal.error({
                title: 'Please Fill Out All Required Fields',
                content: 'Some tiers are missing a name or price',
            });
            return;
        }

        // Filter out tiers that are missing both name and price
        const nonEmptyTiers = tiers.filter(tier => !((!tier.name || tier.name.trim() === '') && (!tier.price || tier.price.trim() === '')));

        if (nonEmptyTiers.length == 1) {
            Modal.error({
                title: 'Multiple Tiers Required',
                content: 'If this event only has one pricing tier, use the default pricing method',
            });
            return;
        }

        // Check if there's at least one tier available until the end of the event
        const tiersWithExpiration = nonEmptyTiers.filter(tier => tier.expiration);
        if (tiersWithExpiration.length > 0) {
            const hasValidTier = nonEmptyTiers.some(tier => !tier.expiration);
            if (!hasValidTier) {
                Modal.error({
                    title: 'Invalid Tier Configuration',
                    content: 'At least one pricing tier must be available throughout the entire event (no expiration date)',
                });
                return;
            }
        }

        // Transform the valid tiers array to match the MongoDB schema format
        const formattedTiers = nonEmptyTiers.map(tier => ({
            name: tier.name.trim(),
            description: tier.name.trim(),
            price: tier.price.trim(),
            expiration: tier.expiration
        }));


        setTiers(nonEmptyTiers)
        // Pass the formatted tiers to onSave
        const [minPrice, maxPrice] = tiers.length ?
            [
                Math.min(...tiers.filter(t => t.price).map(t => Number(t.price))),
                Math.max(...tiers.filter(t => t.price).map(t => Number(t.price)))
            ] :
            [0, 0];

        const incompleteCodes = codes.filter(code =>
            (!code.name || code.name.trim() === '') !== (!code.code || code.code.trim() === '') !== (!code.discount || code.discount.trim() === '')
        );

        if (incompleteCodes.length > 0) {
            Modal.error({
                title: 'Please Fill Out All Required Fields',
                content: 'Some codes are missing a name or discount amount',
            });
            return;
        }

        const nonEmptyCodes = codes.filter(code => !((!code.name || code.name.trim() === '') && (!code.discount || code.discount.trim() === '')));

        // Check for duplicate promo codes
        const duplicateCodes = nonEmptyCodes.filter(code =>
            activePromoCodes.includes(code.name.trim().toUpperCase())
        );

        if (duplicateCodes.length > 0) {
            Modal.error({
                title: 'Duplicate Promotion Code',
                content: `The code ${duplicateCodes[0].name.trim()} is already in use by a an event in your organization. Please choose a different code or inactivate the code through your Stripe dashboard.`,
            });
            return;
        }

        // Check for invalid discount amounts
        for (const code of nonEmptyCodes) {
            const discountAmount = Number(code.discount);
            if (code.discountType === '%' && discountAmount > 100) {
                Modal.error({
                    title: 'Invalid Discount',
                    content: `Discount code "${code.name}" cannot exceed 100%`,
                });
                return;
            }

            // Check if any discount would make prices negative
            if (code.discountType === '$' && discountAmount > minPrice) {
                Modal.error({
                    title: 'Invalid Discount',
                    content: `Discount code "${code.name}" would result in negative prices`,
                });
                return;
            }

            if (code.discountType === '%' && (minPrice * (1 - discountAmount / 100) < 0)) {
                Modal.error({
                    title: 'Invalid Discount',
                    content: `Discount code "${code.name}" would result in negative prices`,
                });
                return;
            }
        }

        const formattedCodes = nonEmptyCodes.map(code => ({
            name: code.name.trim(),
            code: code.code.trim(),
            discount: code.discount.trim(),
            discountType: code.discountType.trim(),
            expiration: code.expiration
        }));

        // Get new promo codes
        const newPromoCodes = nonEmptyCodes.map(code => code.code.trim().toUpperCase());

        // Combine existing and new codes
        const updatedPromoCodes = [...activePromoCodes, ...newPromoCodes];

        // Send update to server
        await axios.post(`${process.env.REACT_APP_SERVER_URL}organizations/update`, {
            name: orgName,
            activePromoCodes: updatedPromoCodes
        }, {
            headers: {
                'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY
            }
        })


        setPrice(minPrice + " - " + maxPrice)
        onSave(formattedTiers, formattedCodes);
        onCancel();
    };


    return (

        <ConfigProvider
            theme={{
                components: {
                    Modal: {
                        titleFontSize: 20,
                        contentBg: 'rgba(75, 75, 75, 0.9)',
                        headerBg: 'transparent',
                        titleColor: '#fff',
                        colorText: '#fff',
                    },
                },
            }}
        >
            <Modal
                title={page}
                style={{ top: 20 }}
                open={isVisible}
                onCancel={onCancel}
                width={800}
                className="ant-modal"
                footer={[
                    <ConfigProvider
                        theme={{
                            components: {
                                Button: {
                                    defaultBg: 'rgba(255, 255, 255, 0.075)',
                                    controlHeight: 36,
                                    borderRadius: 4,
                                    defaultBorderColor: 'rgba(255, 255, 255, 0.075)',
                                    defaultHoverBg: 'rgba(255, 255, 255, 0.1)',
                                    defaultHoverBorderColor: 'rgba(255, 255, 255, 0.1)',
                                    defaultActiveBg: 'rgba(255, 255, 255, 0.01)',
                                    defaultActiveBorderColor: 'rgba(255, 255, 255, 0.075)',
                                    colorText: '#fff',
                                    /* here is your component tokens */
                                },
                            },
                        }}
                    >

                        <Button key="cancel" onClick={onCancel}>
                            Cancel
                        </Button>
                    </ConfigProvider>,

                    <ConfigProvider
                        theme={{
                            components: {
                                Button: {
                                    defaultBg: 'rgba(255, 255, 255, 0.075)',
                                    controlHeight: 36,
                                    borderRadius: 4,
                                    defaultBorderColor: 'rgba(255, 255, 255, 0.075)',
                                    defaultHoverBg: 'rgba(255, 255, 255, 0.1)',
                                    defaultHoverBorderColor: 'rgba(255, 255, 255, 0.1)',
                                    defaultActiveBg: 'rgba(255, 255, 255, 0.01)',
                                    defaultActiveBorderColor: 'rgba(255, 255, 255, 0.075)',

                                    /* here is your component tokens */
                                },
                            },
                        }}
                    >
                        <Button key="save" type="primary" onClick={handleSave}>
                            Save
                        </Button>
                    </ConfigProvider>
                ]}
            >

                <Space direction="vertical" style={{ width: '100%' }}>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>

                        <ConfigProvider
                            theme={{
                                components: {
                                    Segmented: {
                                        trackBg: 'rgba(255, 255, 255, 0.075)',
                                        itemColor: '#ccc',
                                        itemSelectedColor: '#fff',
                                        itemHoverColor: '#fff',
                                        borderRadiusSM: 4,
                                        itemSelectedBg: 'rgba(255, 255, 255, 0.075)',
                                    },
                                },
                            }}
                        >
                            <Segmented
                                size="small"
                                className="org-toggle"
                                options={[
                                    { value: 'Pricing Tiers', label: 'Pricing Tiers' },
                                    { value: 'Discount Codes', label: 'Discount Codes', disabled: false }
                                ]}
                                value={page}
                                onChange={(value) => { setPage(value) }}
                            />
                        </ConfigProvider>

                    </div>

                    {page == "Pricing Tiers" &&
                        <div>
                            {tiers.map((tier, index) => (

                                <ConfigProvider
                                    theme={{
                                        components: {
                                            Card: {
                                                colorBgContainer: 'transparent',
                                                colorBorderSecondary: 'rgba(255, 255, 255, 0.1)',
                                                /* here is your component tokens */
                                            },
                                        },
                                    }}
                                >

                                    <Card
                                        key={tier.id}
                                        style={{ marginBottom: 16, }}

                                    >
                                        <Space direction="vertical" style={{ width: '100%' }}>

                                            <Space style={{ display: 'flex', justifyContent: 'Right' }}>
                                                {index > 0 && (
                                                    <Tooltip title="Move Up">
                                                        <ConfigProvider
                                                            theme={{
                                                                components: {
                                                                    Button: {
                                                                        defaultBg: 'rgba(255, 255, 255, 0.075)',
                                                                        controlHeight: 36,
                                                                        borderRadius: 4,
                                                                        defaultBorderColor: 'rgba(255, 255, 255, 0.075)',
                                                                        defaultHoverBg: 'rgba(255, 255, 255, 0.1)',
                                                                        defaultHoverBorderColor: 'rgba(255, 255, 255, 0.1)',
                                                                        defaultActiveBg: 'rgba(255, 255, 255, 0.01)',
                                                                        defaultActiveBorderColor: 'rgba(255, 255, 255, 0.075)',
                                                                        /* here is your component tokens */
                                                                    },
                                                                },
                                                            }}
                                                        >
                                                            <Button
                                                                icon={<ArrowUpOutlined />}
                                                                onClick={() => moveTier(index, -1)}
                                                            />
                                                        </ConfigProvider>
                                                    </Tooltip>
                                                )}
                                                {index < tiers.length - 1 && (
                                                    <Tooltip title="Move Down">
                                                        <ConfigProvider
                                                            theme={{
                                                                components: {
                                                                    Button: {
                                                                        defaultBg: 'rgba(255, 255, 255, 0.075)',
                                                                        controlHeight: 36,
                                                                        borderRadius: 4,
                                                                        defaultBorderColor: 'rgba(255, 255, 255, 0.075)',
                                                                        defaultHoverBg: 'rgba(255, 255, 255, 0.1)',
                                                                        defaultHoverBorderColor: 'rgba(255, 255, 255, 0.1)',
                                                                        defaultActiveBg: 'rgba(255, 255, 255, 0.01)',
                                                                        defaultActiveBorderColor: 'rgba(255, 255, 255, 0.075)',
                                                                        /* here is your component tokens */
                                                                    },
                                                                },
                                                            }}
                                                        >
                                                            <Button
                                                                icon={<ArrowDownOutlined />}
                                                                onClick={() => moveTier(index, 1)}
                                                            />
                                                        </ConfigProvider>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title="Delete Tier">
                                                    <ConfigProvider
                                                        theme={{
                                                            components: {
                                                                Button: {
                                                                    defaultBg: 'rgba(255, 255, 255, 0.075)',
                                                                    controlHeight: 36,
                                                                    borderRadius: 4,
                                                                    defaultBorderColor: 'rgba(255, 255, 255, 0.075)',
                                                                    defaultHoverBg: 'rgba(255, 255, 255, 0.1)',
                                                                    defaultHoverBorderColor: '#dc4446',
                                                                    defaultActiveBg: 'rgba(255, 255, 255, 0.01)',
                                                                    defaultActiveBorderColor: 'rgba(255, 255, 255, 0.075)',
                                                                    colorText: '#dc4446',
                                                                    /* here is your component tokens */
                                                                },
                                                            },
                                                        }}
                                                    >
                                                        <Button
                                                            icon={<DeleteOutlined />}
                                                            onClick={() => removeTier(tier.id)}

                                                        />
                                                    </ConfigProvider>
                                                </Tooltip>
                                            </Space>
                                            <ConfigProvider
                                                theme={{
                                                    components: {
                                                        Input: {
                                                            colorBgContainer: 'rgba(255, 255, 255, 0.075)',
                                                            colorBorder: 'rgba(255, 255, 255, 0.075)',
                                                            colorText: '#fff',
                                                            colorTextPlaceholder: '#bbb',
                                                            /* here is your component tokens */
                                                        },
                                                    },
                                                }}
                                            >

                                                <Input
                                                    placeholder="Name"
                                                    value={tier.name}
                                                    onChange={(e) => updateTier(tier.id, 'name', e.target.value)}
                                                    style={{
                                                        height: '36px',
                                                        borderRadius: '4px',
                                                        fontFamily: 'Outfit',
                                                    }}
                                                />
                                            </ConfigProvider>

                                            <ConfigProvider
                                                theme={{
                                                    components: {
                                                        Input: {
                                                            colorBgContainer: 'rgba(255, 255, 255, 0.075)',
                                                            colorBorder: 'rgba(255, 255, 255, 0.075)',
                                                            colorText: '#fff',
                                                            colorTextPlaceholder: '#bbb',
                                                            /* here is your component tokens */
                                                        },
                                                    },
                                                }}
                                            >

                                                <Input
                                                    placeholder="Description"
                                                    value={tier.description}
                                                    onChange={(e) => updateTier(tier.id, 'description', e.target.value)}
                                                    style={{
                                                        height: '36px',
                                                        borderRadius: '4px',
                                                        fontFamily: 'Outfit',
                                                    }}
                                                />
                                            </ConfigProvider>

                                            <ConfigProvider
                                                theme={{
                                                    components: {
                                                        Input: {
                                                            colorBgContainer: 'rgba(255, 255, 255, 0.075)',
                                                            colorBorder: 'rgba(255, 255, 255, 0.075)',
                                                            colorText: '#fff',
                                                            colorTextPlaceholder: '#bbb',
                                                            /* here is your component tokens */
                                                        },
                                                    },
                                                }}
                                            >
                                                <Input
                                                    placeholder="Price"
                                                    type="number"
                                                    maxLength="8"
                                                    value={tier.price}
                                                    onChange={(e) => updateTier(tier.id, 'price', e.target.value)}
                                                    style={{
                                                        height: '36px',
                                                        borderRadius: '4px',
                                                        fontFamily: 'Outfit',
                                                    }}
                                                />
                                            </ConfigProvider>


                                                <DatePicker
                                                    className="date-picker"
                                                    // inputReadOnly={isMobile}
                                                    placeholder="Expiration Date (Optional)"
                                                    format="MM/DD/YYYY"
                                                    onChange={(date) => handleTierDateChange(tier.id, date)}
                                                    onOpenChange={(open) => {
                                                        if (open) {
                                                            document.body.classList.add('lock-scroll');
                                                        } else {
                                                            document.body.classList.remove('lock-scroll');
                                                        }
                                                    }}
                                                    placement="bottomLeft"
                                                />

                                        </Space>
                                    </Card>
                                </ConfigProvider>
                            ))}

                            <ConfigProvider
                                theme={{
                                    components: {
                                        Button: {
                                            colorBgContainer: 'rgba(255, 255, 255, 0.075)',
                                            controlHeight: 36,
                                            borderRadius: 4,
                                            defaultBorderColor: 'rgba(255, 255, 255, 0.075)',
                                            defaultHoverBg: 'rgba(255, 255, 255, 0.1)',
                                            defaultHoverBorderColor: 'rgba(255, 255, 255, 0.1)',
                                            defaultActiveBg: 'rgba(255, 255, 255, 0.01)',
                                            defaultActiveBorderColor: 'rgba(255, 255, 255, 0.075)',
                                            defaultColor: '#fff',
                                            defaultHoverColor: '#fff',
                                            /* here is your component tokens */
                                        },
                                    },
                                }}
                            >
                                <Button
                                    type="dashed"
                                    onClick={addTier}
                                    style={{ width: '100%' }}
                                    icon={<PlusOutlined />}
                                >
                                    Add Pricing Tier
                                </Button>
                            </ConfigProvider>
                        </div>
                    }

                    {page == "Discount Codes" &&
                        <div>
                            {codes.map((code, index) => (
                                <ConfigProvider
                                    theme={{
                                        components: {
                                            Card: {
                                                colorBgContainer: 'transparent',
                                                colorBorderSecondary: 'rgba(255, 255, 255, 0.1)',
                                                /* here is your component tokens */
                                            },
                                        },
                                    }}
                                >
                                    <Card
                                        key={code.id}
                                        style={{ marginBottom: 16, }}

                                    >
                                        <Space direction="vertical" style={{ width: '100%' }}>

                                            <Space style={{ display: 'flex', justifyContent: 'Right' }}>
                                                <Tooltip title="Delete Tier">
                                                    <ConfigProvider
                                                        theme={{
                                                            components: {
                                                                Button: {
                                                                    defaultBg: 'rgba(255, 255, 255, 0.075)',
                                                                    controlHeight: 36,
                                                                    borderRadius: 4,
                                                                    defaultBorderColor: 'rgba(255, 255, 255, 0.075)',
                                                                    defaultHoverBg: 'rgba(255, 255, 255, 0.1)',
                                                                    defaultHoverBorderColor: 'rgba(255, 255, 255, 0.1)',
                                                                    defaultActiveBg: 'rgba(255, 255, 255, 0.01)',
                                                                    defaultActiveBorderColor: 'rgba(255, 255, 255, 0.075)',
                                                                    defaultColor: '#fff',
                                                                    defaultHoverColor: '#fff',
                                                                    /* here is your component tokens */
                                                                },
                                                            },
                                                        }}
                                                    >
                                                        <Button
                                                            icon={<DeleteOutlined />}
                                                            onClick={() => removeCode(code.id)}

                                                        />
                                                    </ConfigProvider>
                                                </Tooltip>
                                            </Space>
                                            <ConfigProvider
                                                theme={{
                                                    components: {
                                                        Input: {
                                                            colorBgContainer: 'rgba(255, 255, 255, 0.075)',
                                                            colorBorder: 'rgba(255, 255, 255, 0.075)',
                                                            colorText: '#fff',
                                                            colorTextPlaceholder: '#bbb',
                                                            /* here is your component tokens */
                                                        },
                                                    },
                                                }}
                                            >
                                                <Input
                                                    placeholder="Name"
                                                    value={code.name}
                                                    onChange={(e) => updateCode(code.id, 'name', e.target.value)}
                                                    style={{
                                                        height: '36px',
                                                        borderRadius: '4px',
                                                        fontFamily: 'Outfit',
                                                    }}
                                                />
                                            </ConfigProvider>
                                            <ConfigProvider
                                                theme={{
                                                    components: {
                                                        Input: {
                                                            colorBgContainer: 'rgba(255, 255, 255, 0.075)',
                                                            colorBorder: 'rgba(255, 255, 255, 0.075)',
                                                            colorText: '#fff',
                                                            colorTextPlaceholder: '#bbb',
                                                            /* here is your component tokens */
                                                        },
                                                    },
                                                }}
                                            >

                                                <Input
                                                    placeholder="Code"
                                                    value={code.code}
                                                    onInput={e => e.target.value = e.target.value.toUpperCase()}
                                                    onChange={(e) => updateCode(code.id, 'code', e.target.value)}
                                                    style={{
                                                        height: '36px',
                                                        borderRadius: '4px',
                                                        fontFamily: 'Outfit',
                                                    }}
                                                />
                                            </ConfigProvider>

                                            <div style={{ display: 'flex', gap: 8 }}>
                                                <ConfigProvider
                                                    theme={{
                                                        components: {
                                                            Input: {
                                                                colorBgContainer: 'rgba(255, 255, 255, 0.075)',
                                                                colorBorder: 'rgba(255, 255, 255, 0.075)',
                                                                colorText: '#fff',
                                                                colorTextPlaceholder: '#bbb',
                                                                /* here is your component tokens */
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <Input
                                                        placeholder="Discount"
                                                        type="number"
                                                        maxLength="8"
                                                        value={code.discount}
                                                        onChange={(e) => updateCode(code.id, 'discount', e.target.value)}
                                                        style={{
                                                            height: '36px',
                                                            borderRadius: '4px',
                                                            fontFamily: 'Outfit',
                                                        }}
                                                    />
                                                </ConfigProvider>


                                                <ConfigProvider
                                                    theme={{
                                                        components: {
                                                            Segmented: {
                                                                trackBg: 'rgba(255, 255, 255, 0.075)',
                                                                itemColor: '#ccc',
                                                                itemSelectedColor: '#fff',
                                                                itemHoverColor: '#fff',
                                                                borderRadiusSM: 4,
                                                                itemSelectedBg: 'rgba(255, 255, 255, 0.075)',
                                                                controlHeight: 36,
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <Segmented
                                                        size="medium"
                                                        className="org-toggle"
                                                        style={{ height: '100%' }}
                                                        options={[
                                                            { value: '$', label: '$' },
                                                            { value: '%', label: '%', disabled: false }
                                                        ]}
                                                        value={code.discountType}
                                                        onChange={(value) => updateCode(code.id, 'discountType', value)}
                                                    />
                                                </ConfigProvider>
                                            </div>
                                            <ConfigProvider
                                                theme={{
                                                    components: {
                                                        DatePicker: {
                                                            colorBgContainer: 'rgba(255, 255, 255, 0.075)',
                                                            activeBg: 'rgba(255, 255, 255, 0.075)',
                                                            colorBorder: 'rgba(255, 255, 255, 0.075)',
                                                            colorTextPlaceholder: '#bbb',
                                                            borderRadius: 4,
                                                            controlHeight: 36,
                                                            colorBgElevated: 'rgba(75, 75, 75, 0.95)',
                                                            colorText: '#fff',
                                                            colorTextHeading: '#fff',
                                                            colorIcon: '#bbb',
                                                            colorIconHover: '#fff',
                                                            colorTextDisabled: '#aaa',
                                                            cellActiveWithRangeBg: 'rgba(255, 255, 255, 0.1)',
                                                            fontFamily: 'outfit',
                                                            /* here is your co  ponent tokens */
                                                        },
                                                    },
                                                }}
                                            >

                                                <DatePicker
                                                    className="date-picker"
                                                    // inputReadOnly={isMobile}
                                                    placeholder="Expiration Date (Optional)"
                                                    format="MM/DD/YYYY"
                                                    onChange={(date) => handleCodeDateChange(code.expiration, date)}
                                                    onOpenChange={(open) => {
                                                        if (open) {
                                                            document.body.classList.add('lock-scroll');
                                                        } else {
                                                            document.body.classList.remove('lock-scroll');
                                                        }
                                                    }}
                                                    placement="bottomLeft"
                                                />
                                            </ConfigProvider>
                                        </Space>
                                    </Card>
                                </ConfigProvider>
                            ))}

                            <ConfigProvider
                                theme={{
                                    components: {
                                        Button: {
                                            defaultBg: 'rgba(255, 255, 255, 0.075)',
                                            controlHeight: 36,
                                            borderRadius: 4,
                                            defaultBorderColor: 'rgba(255, 255, 255, 0.075)',
                                            defaultHoverBg: 'rgba(255, 255, 255, 0.1)',
                                            defaultHoverBorderColor: 'rgba(255, 255, 255, 0.1)',
                                            defaultActiveBg: 'rgba(255, 255, 255, 0.01)',
                                            defaultActiveBorderColor: 'rgba(255, 255, 255, 0.075)',
                                            defaultColor: '#fff',
                                            defaultHoverColor: '#fff',
                                            /* here is your component tokens */
                                        },
                                    },
                                }}
                            >
                                <Button
                                    type="dashed"
                                    onClick={addCode}
                                    style={{ width: '100%' }}
                                    icon={<PlusOutlined />}
                                >
                                    Add Discount Code
                                </Button>
                            </ConfigProvider>

                        </div>
                    }

                </Space>
            </Modal>
        </ConfigProvider>
    );
};

export default PricingTiersBuilder;